import {
  useEffect, useRef, useState,
} from 'react';

// New bundle deploys land at most a few times a day; a 15-minute cadence
// surfaces an available update promptly without polling excessively.
const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;
// Focus/visibility events can flap (fast tab-switching, alt-tabbing) — throttle
// them so they don't trigger a check more than once a minute.
const FOCUS_CHECK_THROTTLE_MS = 60 * 1000;
// A hung fetch must not stack across ticks; abort it before the next one starts.
const FETCH_TIMEOUT_MS = 10000;

const MAIN_BUNDLE_HASH_PATTERN = /static\/js\/main\.([0-9a-f]+)\.js/;

export const extractMainBundleHash = (html: string): string | null => {
  const match = html.match(MAIN_BUNDLE_HASH_PATTERN);
  return match ? match[1] : null;
};

interface UseAppVersionCheckOptions {
  // When false, the hook is fully inert — no interval, listeners, or fetches.
  // Defaults to only running in production, where index.html reflects a real
  // deploy rather than the dev server's unhashed bundle.js.
  enabled?: boolean;
}

interface UseAppVersionCheckResult {
  availableHash: string | null;
}

export const useAppVersionCheck = (
  { enabled = process.env.NODE_ENV === 'production' }: UseAppVersionCheckOptions = {},
): UseAppVersionCheckResult => {
  const [ availableHash, setAvailableHash ] = useState<string | null>(null);
  const lastCheckedAtRef = useRef<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const baselineHash = extractMainBundleHash(document.documentElement.outerHTML);
    if (!enabled || baselineHash === null) {
      return undefined;
    }

    const runCheck = async () => {
      // Set before the await so this is the single source of truth for the
      // throttle across every trigger path (interval, focus, visibility, pageshow).
      lastCheckedAtRef.current = Date.now();

      const controller = new AbortController();
      abortControllerRef.current = controller;
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

      try {
        const response = await fetch('/index.html', {
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) {
          return;
        }
        const html = await response.text();
        const latestHash = extractMainBundleHash(html);
        if (latestHash !== null && latestHash !== baselineHash) {
          setAvailableHash(latestHash);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        // A transient offline blip must not kill the poller — log and move on.
        console.error('Update check failed:', error);
      } finally {
        clearTimeout(timeoutId);
      }
    };

    const maybeRunCheck = () => {
      if (Date.now() - lastCheckedAtRef.current >= FOCUS_CHECK_THROTTLE_MS) {
        runCheck();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        maybeRunCheck();
      }
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      // bfcache restores (mainly Safari) don't fire visibilitychange.
      if (event.persisted) {
        maybeRunCheck();
      }
    };

    intervalRef.current = setInterval(runCheck, UPDATE_CHECK_INTERVAL_MS);
    window.addEventListener('focus', maybeRunCheck);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
      window.removeEventListener('focus', maybeRunCheck);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      abortControllerRef.current?.abort();
    };
  }, [ enabled ]);

  return { availableHash };
};
